import { useEffect, useState } from "react";
import {
  TemplateTextTypeModel,
  templateTextTypeApi,
} from "../api/TemplateTextTypeApi";
import { Problem } from "../api/backendApi";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Table, Form, InputGroup } from "react-bootstrap";
import { PlusCircleFill, Search, TrashFill } from "react-bootstrap-icons";
import TemplateTextTypeModal from "../components/modals/TemplateTextTypeModal";
import { useAuth } from "react-oidc-context";
import { authUtils } from "../common/authUtils";

function DefaultTemplateTextTypesPage() {
  const auth = useAuth();
  const [templateTextTypes, setTemplateTextTypes] = useState<
    TemplateTextTypeModel[]
  >([]);
  const [filteredTemplateTextTypes, setFilteredTemplateTextTypes] = useState<
    TemplateTextTypeModel[]
  >([]);
  const [nameFilter, setNameFilter] = useState<string>("");
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [templateTextTypeBeingEdited, setTemplateTextTypeBeingEdited] =
    useState<TemplateTextTypeModel | undefined>(undefined);
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [modalText, setModalText] = useState("");
  const [newTemplateTextType, setNewTemplateTextType] = useState<
    TemplateTextTypeModel | undefined
  >(undefined);

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    templateTextType?: TemplateTextTypeModel,
  ) => {
    setIsCreate(isCreate);
    setModalText(modalText);
    setTemplateTextTypeBeingEdited(templateTextType);
    setNewTemplateTextType(templateTextType);
    setShow(true);
  };

  const selectTemplateTextType = (templateTextType: TemplateTextTypeModel) => {
    if (templateTextType !== templateTextTypeBeingEdited)
      setTemplateTextTypeBeingEdited(templateTextType);
  };

  const onTemplateTextTypeChanged = (
    templateTextType: TemplateTextTypeModel,
  ) => {
    let updatedTemplateTextTypes = templateTextTypes.map(
      (tt: TemplateTextTypeModel) => {
        return tt === templateTextTypeBeingEdited ? templateTextType : tt;
      },
    );

    setTemplateTextTypes(updatedTemplateTextTypes);
    setTemplateTextTypeBeingEdited(undefined);
  };

  const onNewDefaultTemplateTextType = (
    templateTextType: TemplateTextTypeModel,
  ) => {
    setTemplateTextTypes([...templateTextTypes, templateTextType]);

    setNewTemplateTextType(undefined);

    setTimeout(() => {
      var scrollingElement = document.querySelector("div.fixed-table-header");
      scrollingElement?.scrollTo(0, scrollingElement?.scrollHeight);
    }, 50);
  };

  useEffect(() => {
    async function loadTemplateTextTypes() {
      setLoading(true);
      const data = await templateTextTypeApi.getTemplateTextTypes();

      if (data.ok) {
        setError(undefined);
        setTemplateTextTypes(data.value);
        setFilteredTemplateTextTypes(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadTemplateTextTypes();
  }, []);

  useEffect(() => {
    filterTemplateTextTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameFilter, templateTextTypes]);

  const filterTemplateTextTypes = () => {
    var filteredTemplateTextTypes = templateTextTypes;

    filteredTemplateTextTypes = filteredTemplateTextTypes.filter(
      (templateTextType) =>
        templateTextType.name?.toUpperCase().includes(nameFilter.toUpperCase()),
    );

    setFilteredTemplateTextTypes(filteredTemplateTextTypes);
  };

  const deleteTemplateTextType = (
    defaultTemplateTextType: TemplateTextTypeModel,
  ) => {
    templateTextTypeApi
      .deleteTemplateTextType(defaultTemplateTextType.name)
      .then((response) => {
        if (response.ok) {
          setTemplateTextTypes(
            templateTextTypes.filter((tt) => tt !== defaultTemplateTextType),
          );
        } else {
          setError(response.problem);
        }
      });
  };

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Template Text Types
          {authUtils.isAdministrator(auth) && (
            <PlusCircleFill
              className="text-primary"
              onClick={() =>
                handleShow(true, "Add Template Text Type", undefined)
              }
              width={32}
              height={32}
              style={{ marginLeft: "0.5rem" }}
              role="button"
            />
          )}
        </h1>

        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />
        {!loading && (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <InputGroup
              className="sm mb-2 mt-1"
              style={{
                display: "flex",
                width: "fit-content",
                marginRight: "1rem",
              }}
            >
              <span
                className="input-group-text"
                style={{ display: "flex", flexDirection: "row" }}
              >
                <Search width={12} height={12} />
                <Form.Label
                  style={{
                    fontWeight: "400",
                    fontSize: "16px",
                    display: "flex",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginLeft: "5px",
                  }}
                >
                  Name
                </Form.Label>
              </span>
              <Form.Control
                size="sm"
                onChange={(e) => {
                  setNameFilter(e.target.value);
                }}
              ></Form.Control>
            </InputGroup>
          </div>
        )}
        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "95%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover>
                <thead>
                  <tr>
                    <th>
                      <Form.Label
                        style={{
                          marginRight: "1rem",
                          display: "flex",
                          alignItems: "left",
                          justifyContent: "left",
                        }}
                      >
                        Name
                      </Form.Label>
                    </th>
                    <th style={{ width: "1px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTemplateTextTypes.map(
                    (defaultTemplateTextType, index) => (
                      <tr
                        role="button"
                        key={index}
                        onClick={() => {
                          if (authUtils.isAdministrator(auth)) {
                            handleShow(
                              false,
                              "Edit Template Text Type",
                              defaultTemplateTextType,
                            );
                            selectTemplateTextType(defaultTemplateTextType);
                          }
                        }}
                      >
                        <td style={{ whiteSpace: "pre" }}>{defaultTemplateTextType.name}</td>
                        <td>
                          {defaultTemplateTextType.id !== 0 &&
                          authUtils.isAdministrator(auth) ? (
                            <TrashFill
                              className="text-danger"
                              height={24}
                              width={24}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.confirm("Are you sure?")) {
                                  deleteTemplateTextType(
                                    defaultTemplateTextType,
                                  );
                                }
                              }}
                            />
                          ) : null}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
      {show && authUtils.isAdministrator(auth) ? (
        <TemplateTextTypeModal
          templateTextType={
            newTemplateTextType ?? { id: undefined, name: undefined }
          }
          isCreate={isCreate}
          onChange={
            isCreate ? onNewDefaultTemplateTextType : onTemplateTextTypeChanged
          }
          onCancel={() => {
            setShow(false);
            setNewTemplateTextType(undefined);
          }}
          modalText={modalText}
        />
      ) : null}
    </>
  );
}

export default DefaultTemplateTextTypesPage;
